import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import TranscriberForm from "components/AgentWorkspace/TranscriberForm";
import BypassTranscriptionSummary from "components/AgentWorkspace/BypassTranscriptionSummary";
import { useParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import Alert from "react-bootstrap/Alert";
import {
  sendAuthenticatedAsyncRequest,
} from "services/AsyncRequestService";
import Spinner from "react-bootstrap/Spinner";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
import { convertToComponentStructureResponse } from "services/transcriber/transcriberDetailsUtils";
import {
  buildTranscriberDetailsApiPath,
  buildTranscriberPdfApiPath,
  TRANSCRIBER_PROGRAM_CODE,
} from "lookup/TranscriberProgramConstants.jsx";
import { getTranscriberDetailsConfig } from "lookup/TranscriberDetailsConfig";

const STD_DETAILS_CONFIG = getTranscriberDetailsConfig(TRANSCRIBER_PROGRAM_CODE.STD);

const convertToServerResponseFormat = (serverResponse) => {
  const sectionsArray = Object.entries(serverResponse).map(([sectionName, sectionData]) => {
    const { selected, ...fields } = sectionData;

    return {
      sectionName: sectionName,
      selected: selected,
      fields: Object.values(fields)
    };
  });
  const javaResponse = {
    sections: sectionsArray
  }
  return javaResponse;
};

const TranscriberDetails = () => {
  const { transcriptionId } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [responseType, setResponseType] = useState([]);
  const [bypassSummary, setBypassSummary] = useState(null);

  const getTranscriptionData = useCallback((id) => {
    setLoading(true);
    setErrorStatus(false);
    setErrorMessage("");
    setBypassSummary(null);
    setResponseType([]);
    setFormData(null);
    sendAuthenticatedAsyncRequest(
      buildTranscriberDetailsApiPath(TRANSCRIBER_PROGRAM_CODE.STD, id),
      "GET",
      null,
      (r) => {
        if (r.data?.isBypassedAutoDisposition) {
          setBypassSummary(r.data);
          setLoading(false);
          return;
        }
        if (r.data.responseType) {
          setResponseType(r.data.responseType);
        }
        setFormData(convertToComponentStructureResponse(r.data));
        setLoading(false);
      },
      (r) => {
        setLoading(false);
        setErrorStatus(true);
        setErrorMessage("unable to fetch transcription form");
        showNotification({
          title: "Error!",
          message:
            getErrorMessage(r.data) || getErrorMessage("Unable to get the form"),
          position: "tr",
          type: "error",
        });
      }
    );
  }, []);

  useEffect(() => {
    if (transcriptionId) {
      getTranscriptionData(transcriptionId);
    }
  }, [transcriptionId, getTranscriptionData]);

  const sendUpdatedResponse = useCallback((id, updatedResponse) => {
    let serverResponse = convertToServerResponseFormat(updatedResponse);
    sendAuthenticatedAsyncRequest(
      `/agent-workspace/save-transcription/${id}`,
      "POST",
      serverResponse,
      (r) => {
        showNotification({
          title: "Action successful!",
          message: "Form Saved successfully.",
          position: "tc",
          type: "success",
        });
        setLoading(false);
        setErrorStatus(false);
        setErrorMessage("");
        getTranscriptionData(id);
      },
      (r) => {
        setErrorStatus(true);
        setErrorMessage("unable to save the form");
        showNotification({
          title: "Error!",
          message:
            getErrorMessage(r.data) || getErrorMessage("Unable to save the form"),
          position: "tr",
          type: "error",
        });
      }
    );
  }, [getTranscriptionData]);

  const updateSection = useCallback((key, value) => {
    setFormData(currentFormData => {
      if (key.includes('target') && value.selected) {
        const newFormData = { ...currentFormData };
        for (const sectionName in newFormData) {
          if (sectionName.includes("target")) {
            newFormData[sectionName] = {
              ...newFormData[sectionName],
              selected: sectionName === key,
            };
          }
        }
        newFormData[key] = { ...value };
        return newFormData;
      } else {
        return {
          ...currentFormData,
          [key]: { ...value },
        };
      }
    });
  }, []);

  const renderLeftPanel = () => {
    if (bypassSummary) {
      return (
        <BypassTranscriptionSummary
          transcriptionIdLabel={STD_DETAILS_CONFIG.transcriptionIdAlertLabel}
          transcriptionId={transcriptionId}
          isBypassedAutoDisposition={bypassSummary.isBypassedAutoDisposition}
          disposition={bypassSummary.disposition}
          associatedSwitchId={bypassSummary.associatedSwitchId}
        />
      );
    }

    if (responseType && responseType.length > 0) {
      return responseType.map((type, index) => (
        <Alert key={index} variant="danger">
          Response Type: {type}
        </Alert>
      ));
    }

    return (
      <>
        <Alert variant="primary">
          {STD_DETAILS_CONFIG.transcriptionIdAlertLabel}: {transcriptionId}
        </Alert>
        <TranscriberForm
          detailsConfig={STD_DETAILS_CONFIG}
          updateSection={updateSection}
          transcriptionId={transcriptionId}
          formData={formData}
          sendUpdatedResponse={sendUpdatedResponse}
        />
      </>
    );
  };

  if (!loading && !errorStatus && (formData || bypassSummary || responseType.length > 0)) {
    return (
      <Container fluid>
        <Row>
          <Col md={5}>
            {renderLeftPanel()}
          </Col>
          <Col md={7}>
            <ConfigurablePdfViewer
              height="85vh"
              width="100%"
              url={buildTranscriberPdfApiPath(TRANSCRIBER_PROGRAM_CODE.STD, transcriptionId)}
            />
          </Col>
        </Row>
      </Container>
    );
  } else if (errorStatus) {
    return (
      <Alert key="error" variant="danger">
        {errorMessage}
      </Alert>
    );
  } else {
    return <Spinner animation="border" role="status" />;
  }
}

export default connect()(TranscriberDetails);
